import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { NgForOf, NgIf } from '@angular/common';
import { AuditEventSearchRequestDto } from '../../common/interfaces/audit-event-search-request-dto';
import { BusinessHistoryCategory } from '../../common/enums/business-history-category.enum';
import { SecurityDomain } from '../../common/enums/security-domain';
import { getEnterpriseLabels } from '../../common/utils/enterprise-labels.util';
import { defaultAuditDateRange } from '../../common/utils/audit-date-util';
import { Observable, Subscription } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { PrimeTemplate } from 'primeng/api';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { EmptyStateComponent } from '../empty-state/empty-state.component';
import { PaginationComponent } from '../pagination/pagination.component';
import { AuditEventDto } from '../../common/interfaces/audit-event-dto';
import { AuditEventSearchService } from '../../common/services/audit-event-search.service';
import { FormatDateArrayPipe } from '../../common/pipes/format-date-array/format-date-array.pipe';
import { AdminAuditEventType } from '../../common/enums/admin-audit-event-type.enum';
import { getCategoryByEventName } from '../../common/utils/admin-audit-event-utils';
import { SysAdminAuditEventType } from '../../common/enums/sysadmin-audit-event-type.enum';
import { envToken } from '../../common/tokens/env-token';

@Component({
    selector: 'lib-audit-log-table',
    imports: [
        EmptyStateComponent,
        NgForOf,
        NgIf,
        PaginationComponent,
        PrimeTemplate,
        TableModule,
        EmptyStateComponent
    ],
    standalone: true,
    templateUrl: './audit-log-table.component.html',
    styleUrl: './audit-log-table.component.scss'
})
export class AuditLogTableComponent implements OnInit, OnDestroy {
    private readonly auditEventSearchService = inject(AuditEventSearchService);
    private readonly toastrService = inject(ToastrService);
    private env = inject(envToken);

    private SOURCE_SYSTEM_ENTERPRISE_ADMIN = 'ENTERPRISE_ADMIN';

    @Input() enterpriseId = 0;
    @Input() auditLogFilterObs = new Observable<AuditEventSearchRequestDto>;
    @Input() enterpriseLabels = getEnterpriseLabels(SecurityDomain.MODMED);
    @Input() eventCategory?: BusinessHistoryCategory;
    @Input() dataIdentifierPrefix = 'audit-log-table';
    @Input() enterpriseMap?: Map<number, string> = new Map<number, string>();

    totalItems = 0;
    isLoading = false;
    events: AuditEventDto[] = [];
    searchRequest: AuditEventSearchRequestDto = {} as AuditEventSearchRequestDto;
    filterSubscription = new Subscription();
    sourceSystem = this.SOURCE_SYSTEM_ENTERPRISE_ADMIN;

    ngOnInit(): void {
        this.sourceSystem = this.env.sourceSystem;
        this.initializeSearchRequest();
        this.loadEvents();
        this.filterSubscription = this.auditLogFilterObs
            .subscribe((filter) => {
                this.initializeSearchRequest();
                if (Object.keys(filter).length !== 0) {
                    this.searchRequest = { ...this.searchRequest, ...filter };
                }
                if (this.isLoading) return;
                this.loadEvents();
            });
    }

    ngOnDestroy(): void {
        this.filterSubscription.unsubscribe();
    }

    loadEvents(): void {
        this.isLoading = true;
        this.auditEventSearchService.getAuditEvents(this.sourceSystem, this.searchRequest)
            .subscribe({
                next: (response) => {
                    this.totalItems = response.totalElements;
                    //TODO audit-events API adapt needed - shall filter upon (system, sourceSystem)
                    this.events = response.content.filter(item => this.getEventNameLabel(item.eventName) !== '');
                    if (!this.isOrgAdminApp() && !!this.enterpriseMap && this.enterpriseMap?.size > 0) {
                        this.events.forEach(item => {
                            item.enterpriseName = this.enterpriseMap?.get(item.enterpriseId) || '';
                        });
                    }
                    this.isLoading = false;
                },
                error: () => {
                    this.toastrService.error('Failed to retrieve audit logs.', 'Error');
                    this.isLoading = false;
                }
            });
    }

    lazyLoad(event: TableLazyLoadEvent): void {
        this.searchRequest.sortBy = event.sortField ? event.sortField as string : this.searchRequest.sortBy;
        this.searchRequest.sortOrder = event.sortOrder === 1 ? 'ASC' : 'DESC';
        this.loadEvents();
    }

    getEventDateTime(event: AuditEventDto): string {
        return event?.eventDate ? new FormatDateArrayPipe().transform(event?.eventDate, true) : '';
    }

    getEventNameLabel(eventName: string): string {
        const label = (this.isOrgAdminApp() ? AdminAuditEventType[ eventName as keyof typeof AdminAuditEventType ] :
            SysAdminAuditEventType[ eventName as keyof typeof SysAdminAuditEventType ]) ?? '';
        return label.includes('Universal ID')
            ? label.replace(/Universal ID/g, this.enterpriseLabels.universalIdLabel)
            : label;
    }

    getEventCategory(eventName: string): string {
        const labelOverrides: Record<string, string> = {
            'Users': this.enterpriseLabels.universalIdsLabel,
            'Accounts': this.enterpriseLabels.accountsLabel
        };
        const category = getCategoryByEventName(eventName);
        return labelOverrides[ category ] || category;
    }

    getEventDetails(eventDetail: string): string[] {
        return eventDetail.split('. ');
    }

    onSelectItemsPerPage(itemsPerPage: number): void {
        this.searchRequest.pageRequest.size = itemsPerPage;
    }

    onChangeCurrentPage(currentPage: number): void {
        this.searchRequest.pageRequest.page = currentPage;
        this.loadEvents();
    }

    initializeSearchRequest(): void {
        this.searchRequest = {
            pageRequest: {
                page: 1,
                size: 25
            },
            sortBy: 'EVENT_TIME',
            sortOrder: 'DESC',
            partitionId: this.isOrgAdminApp() && this.enterpriseId > 0 ? this.enterpriseId : 0,
            eventTimeRange: defaultAuditDateRange(),
            attributes: this.eventCategory ? { category: this.eventCategory } : {}
        } as AuditEventSearchRequestDto;
    }

    isOrgAdminApp(): boolean {
        return this.sourceSystem === this.SOURCE_SYSTEM_ENTERPRISE_ADMIN;
    }
}
